import { Button } from "@kart/components";
import dayjs from "dayjs";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getAllSessionsForTrackAndUser } from "../utils/Session/Session.axios";
import { Session } from "../utils/time/time.types";
import { getAllTracksForUser } from "../utils/track/Track.axios";
import { Track } from "../utils/track/Track.types";
import { useAxios } from "../hooks/useAxios";
import { useUser } from "../hooks/useUser";

export const Dashboard = () => {
  const { axios } = useAxios();
  const { user } = useUser();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<string>();
  const [sessions, setSessions] = useState<Session[]>([]);

  useEffect(() => {
    if (!user?.id || !axios) return;
    getAllTracksForUser(axios, user.id).then(setTracks);
  }, [user?.id, axios]);

  useEffect(() => {
    if (!selectedTrack || !user || !axios) return;
    getAllSessionsForTrackAndUser(axios, selectedTrack, user.id).then(
      setSessions,
    );
  }, [axios, selectedTrack, user]);

  const fastestLap = useMemo(() => {
    return sessions
      .sort((a, b) => (dayjs(a.date).isBefore(b.date) ? -1 : 1))
      .map((session) => ({
        name: new Date(session.date).toLocaleDateString("DE-de"),
        fastestLap: session.fastestLap,
      }));
  }, [sessions]);

  const formatTime = (ms: number) => `${(ms / 1000).toFixed(3)}s`;

  const timingChart: ReactNode = useMemo((): ReactNode => {
    if (!selectedTrack) return <></>;
    return (
      <div className="dashboard-page__table-wrapper">
        <p className="dashboard-page__table-wrapper__title">
          Bestzeit pro Session
        </p>
        <LineChart className="table" width={350} height={200} data={fastestLap}>
          <Line dataKey={"fastestLap"} strokeWidth={3} />
          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey="name" />
          <YAxis
            tickFormatter={formatTime}
            domain={["dataMin - 1000", "dataMax + 1000"]}
          />
          <Tooltip formatter={(value: number) => formatTime(value)} />
        </LineChart>
      </div>
    );
  }, [fastestLap, selectedTrack]);

  const position = useMemo(() => {
    return sessions
      .filter((session) => session.position !== 0)
      .sort((a, b) => (dayjs(a.date).isBefore(b.date) ? -1 : 1))
      .map((session) => ({
        name: new Date(session.date).toLocaleDateString("DE-de"),
        position: session.position,
        competitors: session.competitors,
      }));
  }, [sessions]);
  const competitors: number = useMemo(
    () =>
      sessions.sort((a, b) => b.competitors - a.competitors)[0]?.competitors ||
      0,
    [sessions],
  );

  const positionChart: ReactNode = useMemo((): ReactNode => {
    if (!selectedTrack) return <></>;
    return (
      <div className="dashboard-page__table-wrapper">
        <p className="dashboard-page__table-wrapper__title">
          Platzierung der Session
        </p>
        <LineChart width={350} height={200} data={position} className="table">
          <Line dataKey={"position"} strokeWidth={2} />
          <Line dataKey={"competitors"} opacity={"50%"} />
          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey="name" />
          <YAxis domain={[1, competitors + 2]} />
          <Legend />
          <Tooltip />
        </LineChart>
      </div>
    );
  }, [competitors, position, selectedTrack]);

  return (
    <div className="dashboard-page">
      <h1 className="dashboard-page__title">Meine Statistiken</h1>
      <h2 className="dashboard-page__track-titles">Meine Strecken</h2>
      <div className="dashboard-page__track-buttons">
        {tracks.map((track) => (
          <Button
            label={track.name}
            onClick={() => setSelectedTrack(track.id)}
          />
        ))}
      </div>
      <div className="tables">
        <div className="table-wrapper">{timingChart}</div>
        <div className="table-wrapper">{positionChart}</div>
      </div>
    </div>
  );
};
